import React, { Suspense } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Provider, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router';

import { ToastContainer } from '@private/notifications';
import { initDomPurifyHooks } from '@private/payment';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ReactRouterAdapter } from 'configs/queryParamsAdapter';
import { PersistGate } from 'redux-persist/integration/react';
import { ThemeContextProvider } from 'ThemeContext';
import { QueryParamProvider } from 'use-query-params';

import { IAuthState } from 'api/auth/types';
import { useAppDispatch } from 'hooks/storeHooks';
import { usePortalTheme } from 'hooks/usePortalTheme';
import { StyledModalProvider } from 'modals';
import { queryClient } from 'queries';
import { SESSION_KEYS } from 'queries/session/constants';
import { useQueryData } from 'queries/utils';
import IndexRoute from 'routes';
import ErrorBoundary from 'routes/ErrorBoundary';
import store, { persistor, TRootState } from 'store';
import { saveAppWidth } from 'store/application';
import { IUserState } from 'store/userConfiguration';
import { GlobalReset } from 'theme/reset';
import { initSentry } from 'utils/sentryLazy';

import 'theme/fonts.css';
import './configs/i18n';
import './configs/DOMPurify';

if (typeof requestIdleCallback !== 'undefined') {
  requestIdleCallback(() => initSentry());
} else {
  setTimeout(initSentry, 2000);
}

initDomPurifyHooks();

interface IAppProps {
  setCssVariables: (variables: string) => void;
}

const App = ({ setCssVariables }: IAppProps): React.ReactElement => {
  const { i18n, t } = useTranslation();
  const { composedTheme, stringVariables } = usePortalTheme();

  const dispatch = useAppDispatch();

  const session = useQueryData<IAuthState>([SESSION_KEYS.SESSION]);
  const { language, unauthorizedLanguage } = useSelector<TRootState, IUserState>(
    (state) => state.userConfiguration,
  );

  React.useEffect(() => {
    const handleResize = () => dispatch(saveAppWidth(window.innerWidth));
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    setCssVariables(stringVariables);
  }, [setCssVariables, stringVariables]);

  React.useEffect(() => {
    const isAuthorized = session?.token;
    i18n.changeLanguage(isAuthorized ? language : unauthorizedLanguage);
  }, [session?.token, language, unauthorizedLanguage]);

  const { orgnSettings } = useSelector<TRootState, IUserState>(
    (state) => state.userConfiguration,
  );

  const { logo, name: portalName } = orgnSettings || {};

  const themeContextValue = React.useMemo(
    () => ({ theme: composedTheme }),
    [composedTheme],
  );

  return (
    <HelmetProvider>
      <Helmet>
        <title>{orgnSettings ? portalName || t('logo') : ''}</title>
        <meta content={orgnSettings ? portalName || t('logo') : ''} name='description' />
        {orgnSettings && <link href={logo || '/favicon/favicon.ico'} rel='icon' />}
      </Helmet>
      <ReactQueryDevtools initialIsOpen={false} position='bottom' />
      <ToastContainer closeButton={false} hideProgressBar pauseOnFocusLoss={false} />
      <ThemeContextProvider value={themeContextValue}>
        <StyledModalProvider>
          <IndexRoute />
        </StyledModalProvider>
      </ThemeContextProvider>
    </HelmetProvider>
  );
};

const AppRoot = () => {
  const [cssVariables, setCssVariables] = React.useState('');

  const setVariables = React.useCallback((variables: string) => {
    setCssVariables(variables);
  }, []);

  // eslint-disable-next-line
  console.log('APP_VERSION: ', window.CONFIG.APP_VERSION);

  return (
    <Suspense fallback={null}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <BrowserRouter>
            <QueryParamProvider adapter={ReactRouterAdapter}>
              <QueryClientProvider client={queryClient}>
                <GlobalReset cssVariables={cssVariables} />
                <ErrorBoundary>
                  <App setCssVariables={setVariables} />
                </ErrorBoundary>
              </QueryClientProvider>
            </QueryParamProvider>
          </BrowserRouter>
        </PersistGate>
      </Provider>
    </Suspense>
  );
};

export default AppRoot;
